/**
 * Bundle builder remake
 * Extends Biscuits app behavior by injecting custom details into bundle items.
 */

class BundleBuilderCustomDetails {
  constructor() {
    this.detailsSelector = ".biscuits-bundle-custom-detail";

    document.addEventListener("BiscuitsBundleProduct:ready", (event) => {
      this.handleProductReady(event);
    });
    document.addEventListener("BiscuitsBundleVariant:ready", (event) => {
      this.handleProductReady(event);
    });
    document.addEventListener("BiscuitsBundleForm:ready", (event) => {
      this.customUpdateTotalPrice(event);
    });
  }

  handleProductReady(event) {
    const target = event.target;
    if (!target || target.dataset.biscuitsAvailable === 'false') return;

    const productWrapper = target.closest(".biscuits-bundle-item-card");
    if (!productWrapper) return;

    const jsonScript = productWrapper.querySelector("script.biscuits-bundle-item__json");

    if (!jsonScript) return;

    let productJson;
    try {
      productJson = JSON.parse(jsonScript.textContent.trim());

    } catch (e) {
      console.warn("Invalid JSON in .biscuits-bundle-item__json", e);
      return;
    }

    const id = productJson[0]?.product_id || productJson?.product_id;

    if (!id) return;

    // Find matching custom detail wrapper
    const detailWrapper = document.querySelector(
      `${this.detailsSelector}[data-biscuits-product-id="${id}"]`
    );

    if (!detailWrapper) return;

    this.injectDetails(detailWrapper, productWrapper);
  }

  injectDetails(detailWrapper, bundleItem) {
    if (!bundleItem) return;

    const imageContainer = bundleItem.querySelector(".biscuits-bundle-item__image");
    const titleElement = bundleItem.querySelector(".biscuits-bundle-item__title");
    const priceElement = bundleItem.querySelector("biscuits-bundle-price");

    if (!imageContainer || !titleElement) return;

    // Pre-fetch all nodes once
    const nodes = {
      badges: detailWrapper.querySelector(".biscuits-bundle-custom-detail__badges"),
      ages: detailWrapper.querySelector(".biscuits-bundle-custom-detail__ages"),
      world: detailWrapper.querySelector(".biscuits-bundle-custom-detail__world"),
      usps: detailWrapper.querySelector(".biscuits-bundle-custom-detail__usps"),
      labels: detailWrapper.querySelector(".biscuits-bundle-custom-detail__labels"),
      reviews: detailWrapper.querySelector(".biscuits-bundle-custom-detail__reviews"),
      price: detailWrapper.querySelector(".biscuits-bundle-custom-detail__price")
    };

    const clone = (el) => (el ? el.cloneNode(true) : null);

    // Insert into image container (badges, world, labels)
    ["badges", "world", "labels"].forEach((key) => {
      if (nodes[key]) imageContainer.appendChild(clone(nodes[key]));
    });
    // Insert before title (ages, reviews)
    ["ages", "reviews"].forEach((key) => {
      if (nodes[key])
        titleElement.insertAdjacentElement("beforebegin", clone(nodes[key]));
    });
    // Insert after price
    if (nodes["price"])
      priceElement.insertAdjacentElement("afterend", clone(nodes["price"]));
    // Insert after title (usps)
    if (nodes.usps)
      titleElement.insertAdjacentElement("afterend", clone(nodes.usps));
  }

  calculateNewTotalDiscount(bundleForm) {
    const volumeDiscountsContainer = bundleForm.querySelector('.biscuits-bundle-volume-discounts__container');
    const tiers = volumeDiscountsContainer ? volumeDiscountsContainer.querySelectorAll('.biscuits-bundle-volume-discount-tier') : [];

    // Get total products (ensure the element selector is correct based on your previous code)
    const totalProductsElement = bundleForm.querySelector('biscuits-bundle-volume-discounts');
    const totalProducts = parseInt(totalProductsElement ? totalProductsElement.getAttribute("data-total") : 0, 10);

    let maxDiscountPercentage = 0;

    tiers.forEach(tier => {
      const requiredQuantity = parseInt(tier.getAttribute('data-biscuits-quantity'), 10);
      const discountValue = parseFloat(tier.getAttribute('data-biscuits-discount-value')); // This should be a percentage (e.g., 0.10 for 10%) or 10.

      // Assuming data-biscuits-discount-value is a percentage (e.g., 10, 20, 30)
      let discountPercentage = discountValue;

      if (totalProducts >= requiredQuantity) {
        if (discountPercentage > maxDiscountPercentage) {
          maxDiscountPercentage = discountPercentage;
        }
      }
    });

    let discountFactor = 1;
    if (maxDiscountPercentage > 0) {
      // Convert percentage (e.g., 10) to a factor (e.g., 0.90)
      discountFactor = 1 - (maxDiscountPercentage / 100);
    }

    // Apply the calculated discount
    return discountFactor;
  }

  calculateBundleTotal() {
    const bundleForm = document.querySelector('biscuits-bundle-form');
    if (!bundleForm) {
      // Exit if the main form element is not found
      console.warn('Biscuits bundle form element not found. Exiting calculateBundleTotal.');
      return;
    }

    let totalBundlePrice = 0;

    // Select all currently active items in the bundle
    const activeItems = bundleForm.querySelectorAll('.biscuits-bundle-item.biscuits--active');

    if (activeItems.length !== 0) {
      activeItems.forEach((item) => {
        // Get quantity from the input field
        const quantityInput = item.querySelector('.biscuits-bundle-quantity__input');
        const quantity = parseInt(quantityInput ? quantityInput.value : 1, 10);

        // Find the element containing the price data (it could be the item itself or a child)
        // We retrieve the price from the 'data-biscuits-price' attribute.
        const priceElement = item.querySelector('[data-biscuits-price]') || item;
        const priceData = priceElement.getAttribute('data-biscuits-price');

        if (!priceData) {
          console.warn(`Bundle item ${item.dataset.productId || 'N/A'} is missing data-biscuits-price.`);
          return;
        }

        const unitPrice = parseFloat(priceData);

        if (isNaN(unitPrice)) {
          console.error(`Cannot convert price value '${priceData}' to a number.`);
          return;
        }

        const linePrice = unitPrice * quantity;
        totalBundlePrice += linePrice;
      });
    }

    totalBundlePrice *= this.calculateNewTotalDiscount(bundleForm);
    const formattedPrice = window.formatMoney ? window.formatMoney(totalBundlePrice) : false;
    this.showCustomDiscountField(bundleForm);

    // --- DOM UPDATE LOGIC ---
    // Update the total display element only if the calculated price is greater than 0
    if (totalBundlePrice) {
      // Target selector for the final total display area
      const totalDisplayElement = bundleForm.querySelector('.biscuits-bundle-totals__row.biscuits-bundle-totals__row--total .biscuits-bundle-form__total');

      if (totalDisplayElement) {
        // Update the HTML content with the formatted price
        totalDisplayElement.innerHTML = formattedPrice;
      }
    }
  }

  showCustomDiscountField(bundleForm) {
    // 1. Find the custom discount value
    const customDiscountElement = bundleForm.querySelector('[data-biscuits-custom-discount]');
    const customDiscountValue = customDiscountElement ? customDiscountElement.getAttribute('data-biscuits-custom-discount') : null;

    if (customDiscountValue) {
      // Find the original discount row to duplicate
      const originalDiscountRow = bundleForm.querySelector('.biscuits-bundle-totals__row.biscuits-bundle-totals__row--discount');

      if (originalDiscountRow) {
        const customDiscountClass = 'biscuits-bundle-totals__row--custom-discount';
        let customDiscountRow = bundleForm.querySelector(`.${customDiscountClass}`);

        // Check if the custom discount row already exists to avoid duplication
        if (!customDiscountRow) {
          // 2. Duplicate the element
          customDiscountRow = originalDiscountRow.cloneNode(true);

          // Add a unique class for future identification and styling
          customDiscountRow.classList.add(customDiscountClass);

          // 3. Insert the duplicate immediately after its parent (the original row's parent)
          originalDiscountRow.parentNode.insertBefore(customDiscountRow, originalDiscountRow.nextSibling);
        }

        // 4. Remove unwanted attributes from the duplicate
        customDiscountRow.removeAttribute('aria-posinset');

        // 5. & 6. Find the discount display element in the duplicate and update the value
        const discountDisplay = customDiscountRow.querySelector('.biscuits-bundle-form__discount');

        if (discountDisplay) {
          // Construct the new discount text
          // Ensure value is numeric before formatting
          const discountText = parseFloat(customDiscountValue) > 0
            ? `-${customDiscountValue}%`
            : `${customDiscountValue}`;

          discountDisplay.innerHTML = discountText;
        }
      }
    }
  }

  customUpdateTotalPrice() {
    if (document.querySelector("biscuits-bundle-form .biscuits-bundle-custom-detail__price")) {
      document.querySelectorAll('.biscuits-bundle-item').forEach((item) => {
        item.addEventListener('biscuits--product-selection', () => {
          setTimeout(() => {
            this.calculateBundleTotal();
          }, 1000);
        });
      });
    }
  }
}

window.BundleBuilderCustomDetails = new BundleBuilderCustomDetails();